/**
 * 65.27 (DECISIONS 185's step 1; the owner: "the concept of hit locations for shots when infantry is shot. Difference
 * between headshot, body shot, limb shot"): where a round meets a person — read from the hit point in the person's own
 * frame (core/bullets.ts personFrame, the hit test's): its height over the feet as a share of the stance's body (the top
 * of its capsule), the head at the top, the limbs below the hips or (on the measured body, decision 132) out at its sides
 * (73.4: along its side axis); prone, along the body's length. It works on today's sphere and on the measured capsule alike. Pure.
 */
import type { GameConfig, HitLocation, PlanetState, PlayerState, Vec3 } from '../types';
import { HIT_LOCATION } from '../constants';
import { vecDot, vecSub } from '../math';
import { personFrame } from './bullets';
import type { PersonSet } from './scaleProfile';

/** Where `point` (on or in person `p`'s hit shape) lands on it. */
export function hitLocation(p: PlayerState, point: Vec3, planets: PlanetState[], ps: PersonSet, measured: boolean): HitLocation {
  const { parts, P, feet, n, h, r } = personFrame(p, planets, ps);
  const cap = parts.find(q => q.shape === 'capsule') as { a: [number, number, number]; b: [number, number, number]; radius: number } | undefined;
  if (!cap) return 'body';
  const rel = vecSub(point, feet), L = HIT_LOCATION;
  if (p.stance === 'prone' && !p.airborne) {
    // along the body: its back end to its front (the capsule along the heading, its caps included)
    const z0 = Math.min(cap.a[2], cap.b[2]) - cap.radius, z1 = Math.max(cap.a[2], cap.b[2]) + cap.radius;
    const f = (vecDot(rel, h) / P - z0) / (z1 - z0);
    return f >= 1 - L.proneHead ? 'head' : f < L.proneLimbs ? 'limbs' : 'body';
  }
  const top = Math.max(cap.a[1], cap.b[1]) + cap.radius, up = vecDot(rel, n) / P, f = up / top;
  const band = p.stance === 'crouch' && !p.airborne ? L.crouch : L;   // (73.3: a crouch's own bands — the head a larger share of a folded body)
  if (f >= band.headFrom) return 'head';
  if (f < band.limbsBelow) return 'limbs';
  // the arms at the body's sides: on the measured body, a point well out along its side axis — the chest and the back are
  // the torso, every surface point a radius from the axis (73.4: read off the axis in every direction, they read limbs too);
  // the sphere's side is the torso
  if (measured && Math.abs(vecDot(rel, r) / P - cap.a[0]) > L.armsOff * cap.radius) return 'limbs';
  return 'body';
}

/** × a round's damage where it landed — the Lab's head and limb dials, else HIT_LOCATION's. */
export function locationMultiplier(config: Pick<GameConfig, 'hitHead' | 'hitLimbs'>, loc: HitLocation): number {
  return loc === 'head' ? config.hitHead ?? HIT_LOCATION.multiplier.head : loc === 'limbs' ? config.hitLimbs ?? HIT_LOCATION.multiplier.limbs : HIT_LOCATION.multiplier.body;
}
